import {
    Box,
    Button,
    Collapse,
    Dialog,
    DialogContent,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha, darken, type Theme } from '@mui/material/styles';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChangePasswordMutation } from '../../../../app/Slices/SevenDayApiSlice';
import toast from '../../../shared/Toast';
import { z } from 'zod';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ModalProps } from '../../../types/modal';
import {
    CheckCircle,
    Close,
    Visibility,
    VisibilityOff,
} from '@mui/icons-material';
import SecurityIcon from '@mui/icons-material/Security';

/** Matches `Colors.Coral` in shared/theme.ts */
const CORAL = '#E76F51';

/** Same rules as previous modal; used for Zod + UI checklist. */
function computePasswordRules(password: string) {
    return {
        length: password.length >= 8,
        lower: /[a-z]/.test(password),
        upper: /(?=.*[A-Z])/.test(password),
        number: /[0-9]/.test(password),
        special: /[#?!@$%^&*-]/.test(password),
    };
}

function isPasswordValid(password: string) {
    const r = computePasswordRules(password);
    return r.length && r.lower && r.upper && r.number && r.special;
}

const changePasswordFormSchema = z
    .object({
        currentPassword: z.string().min(1, { message: 'Required' }),
        newPassword: z
            .string()
            .min(1, { message: 'Required' })
            .refine((val) => isPasswordValid(val), {
                message: 'Invalid password',
            }),
        confirmNewPassword: z.string().min(1, { message: 'Required' }),
    })
    .refine((data) => data.newPassword === data.confirmNewPassword, {
        message: 'Passwords do not match',
        path: ['confirmNewPassword'],
    })
    .refine((data) => data.newPassword !== data.currentPassword, {
        message: 'Cannot be the same as current password',
        path: ['newPassword'],
    });

type ChangePassword = z.infer<typeof changePasswordFormSchema>;

function strengthLabel(validCount: number): string {
    if (validCount === 5) return 'Good';
    if (validCount <= 2) return 'Weak';
    return 'Fair';
}

function strengthColor(theme: Theme, validCount: number): string {
    const sapphire = theme.palette.custom.sapphire;
    if (validCount <= 2) return theme.palette.warning.main;
    if (validCount <= 4) return theme.palette.secondary.main;
    return sapphire;
}

export default function EditPasswordModal({ open, onClose }: ModalProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [changePassword] = useChangePasswordMutation();
    const navigate = useNavigate();

    const [cancel, setCancel] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const methods = useForm<ChangePassword>({
        mode: 'onChange',
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
        },
        resolver: zodResolver(changePasswordFormSchema),
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty, isValid },
    } = methods;

    const newPasswordW = useWatch({ control, name: 'newPassword' });
    const confirmW = useWatch({ control, name: 'confirmNewPassword' });

    const rules = useMemo(
        () => computePasswordRules(newPasswordW || ''),
        [newPasswordW],
    );

    const passwordRequirements = useMemo(
        () => [
            { valid: rules.length, message: 'Minimum 8 characters' },
            { valid: rules.lower, message: 'At least one lowercase letter' },
            { valid: rules.upper, message: 'At least one uppercase letter' },
            { valid: rules.number, message: 'At least one number' },
            {
                valid: rules.special,
                message: 'At least one special character (!@#?&)',
            },
        ],
        [rules],
    );

    const validCount = useMemo(
        () => passwordRequirements.filter((r) => r.valid).length,
        [passwordRequirements],
    );

    /** Four segments only fully fill when every requirement is met. */
    const filledStrengthBars =
        validCount <= 0 ? 0 : validCount >= 5 ? 4 : Math.min(validCount, 3);
    const confirmMatches =
        Boolean(newPasswordW) && Boolean(confirmW) && newPasswordW === confirmW;

    useEffect(() => {
        if (!open) {
            setError('');
            setCancel(false);
            setShowCurrent(false);
            setShowNew(false);
            setShowConfirm(false);
        }
    }, [open]);

    const handleClose = () => {
        if (isDirty) {
            if (!cancel) {
                setCancel(true);
                return;
            }
        }

        if (cancel) reset();

        setCancel(false);
        onClose();
    };

    const onNestedSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
        event.preventDefault();
        if (event && typeof event.stopPropagation === 'function') {
            event.stopPropagation();
        }

        return handleSubmit(onSubmit)(event);
    };

    const onSubmit = (data: ChangePassword) => {
        changePassword(data).then((response: any) => {
            if (response) {
                if (response?.error?.data !== 'Password is changed.') {
                    setError(response?.error?.data?.message);
                } else {
                    toast.success(`Password has been changed`);
                    navigate(`/profile`);
                    onClose();
                }
            } else {
                setError(response?.error?.data?.message);
            }
        });
    };

    const sapphire = theme.palette.custom.sapphire;
    const coral = theme.palette.custom.coral;

    const headerIcon = (
        <Box
            sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: alpha(sapphire, 0.16),
                color: darken(sapphire, 0.22),
            }}
        >
            <SecurityIcon sx={{ fontSize: 26 }} />
        </Box>
    );

    const passwordField = (
        name: keyof Pick<
            ChangePassword,
            'currentPassword' | 'newPassword' | 'confirmNewPassword'
        >,
        label: string,
        show: boolean,
        toggle: () => void,
        options?: { showMatch?: boolean; hideLabel?: boolean },
    ) => (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error: fieldError } }) => (
                <Stack spacing={0.75}>
                    {!options?.hideLabel ? (
                        <InputLabel
                            sx={{
                                typography: 'body2',
                                fontWeight: 600,
                                color: 'text.primary',
                            }}
                        >
                            {label}
                        </InputLabel>
                    ) : null}
                    <TextField
                        {...field}
                        type={show ? 'text' : 'password'}
                        size='small'
                        fullWidth
                        error={!!fieldError}
                        helperText={fieldError?.message}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position='end'>
                                    {options?.showMatch && confirmMatches ? (
                                        <CheckCircle
                                            sx={{
                                                color: sapphire,
                                                mr: 0.5,
                                                fontSize: 22,
                                            }}
                                        />
                                    ) : null}
                                    <IconButton
                                        aria-label={
                                            show
                                                ? 'Hide password'
                                                : 'Show password'
                                        }
                                        onClick={toggle}
                                        edge='end'
                                        size='small'
                                    >
                                        {show ? (
                                            <VisibilityOff fontSize='small' />
                                        ) : (
                                            <Visibility fontSize='small' />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Stack>
            )}
        />
    );

    const requirementsPanel = (
        <Box
            sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(sapphire, 0.45)}`,
                bgcolor: alpha(sapphire, 0.08),
                p: 2,
            }}
        >
            <Grid container spacing={1.5}>
                {passwordRequirements.map((requirement) => (
                    <Grid item xs={12} sm={6} key={requirement.message}>
                        <Stack direction='row' alignItems='center' spacing={1}>
                            <CheckCircle
                                sx={{
                                    fontSize: 20,
                                    color: requirement.valid
                                        ? sapphire
                                        : alpha(
                                              theme.palette.text.secondary,
                                              0.35,
                                          ),
                                }}
                            />
                            <Typography
                                variant='body2'
                                color={
                                    requirement.valid
                                        ? 'text.primary'
                                        : 'text.secondary'
                                }
                            >
                                {requirement.message}
                            </Typography>
                        </Stack>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    const footerActions = (
        <Stack
            direction='row'
            spacing={1.5}
            justifyContent='space-between'
            alignItems='center'
            sx={{
                pt: 2.5,
                mt: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Button
                type='button'
                variant='outlined'
                onClick={handleClose}
                sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: 'divider',
                    color: 'text.primary',
                    px: 2,
                }}
            >
                Cancel
            </Button>
            <Button
                type='submit'
                variant='contained'
                disabled={!isValid}
                sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                    boxShadow: 'none',
                    bgcolor: theme.palette.dark.main,
                    '&:hover': {
                        boxShadow: 'none',
                        bgcolor: alpha(theme.palette.dark.main, 0.88),
                    },
                }}
            >
                Update Password
            </Button>
        </Stack>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth='sm'
            scroll='paper'
            PaperProps={{
                elevation: 0,
                sx: {
                    borderRadius: isMobile ? '20px 20px 0 0' : 3,
                    overflow: 'hidden',
                    ...(isMobile && {
                        m: 0,
                        maxWidth: '100%',
                        width: '100%',
                        position: 'fixed',
                        bottom: 0,
                        maxHeight: 'min(92vh, 900px)',
                    }),
                },
            }}
            sx={{
                '& .MuiDialog-container': {
                    alignItems: isMobile ? 'flex-end' : 'center',
                },
            }}
        >
            <FormProvider {...methods}>
                <form onSubmit={onNestedSubmit}>
                    <Box
                        sx={{
                            px: { xs: 2, sm: 3 },
                            pt: isMobile ? 1 : 3,
                            pb: isMobile
                                ? `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`
                                : 3,
                        }}
                    >
                        {isMobile && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mb: 1.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 4,
                                        borderRadius: 2,
                                        bgcolor: 'action.disabledBackground',
                                    }}
                                />
                            </Box>
                        )}
                        <Stack
                            direction='row'
                            alignItems='flex-start'
                            justifyContent='space-between'
                            spacing={2}
                            sx={{ mb: 2.5 }}
                        >
                            <Stack
                                direction='row'
                                spacing={2}
                                alignItems='flex-start'
                                flex={1}
                                minWidth={0}
                            >
                                {headerIcon}
                                <Box minWidth={0}>
                                    <Typography
                                        variant='h6'
                                        fontWeight={700}
                                        color='text.primary'
                                    >
                                        Change Password
                                    </Typography>
                                    <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        sx={{ mt: 0.5 }}
                                    >
                                        Choose a strong password to keep your
                                        account secure.
                                    </Typography>
                                </Box>
                            </Stack>
                            <IconButton
                                aria-label='Close'
                                onClick={handleClose}
                                size='small'
                                sx={{ mt: -0.5, color: 'text.secondary' }}
                            >
                                <Close />
                            </IconButton>
                        </Stack>

                        <DialogContent>
                            <Collapse in={cancel}>
                                <Box
                                    sx={{
                                        mb: 2,
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: alpha(
                                            theme.palette.warning.main,
                                            0.12,
                                        ),
                                        border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
                                    }}
                                >
                                    <Typography
                                        variant='body2'
                                        color='text.primary'
                                    >
                                        You have unsaved changes. Are you sure
                                        you want to cancel?
                                    </Typography>
                                    <Stack
                                        direction='row'
                                        spacing={1}
                                        sx={{ mt: 1.5 }}
                                    >
                                        <Button
                                            size='small'
                                            variant='outlined'
                                            onClick={() => setCancel(false)}
                                        >
                                            Keep editing
                                        </Button>
                                        <Button
                                            size='small'
                                            variant='text'
                                            color='warning'
                                            onClick={() => {
                                                reset();
                                                setCancel(false);
                                                onClose();
                                            }}
                                        >
                                            Discard
                                        </Button>
                                    </Stack>
                                </Box>
                            </Collapse>
                            <Collapse in={error !== ''}>
                                <Box
                                    sx={{
                                        mb: 2,
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: alpha(CORAL, 0.08),
                                        border: `1px solid ${alpha(CORAL, 0.35)}`,
                                    }}
                                >
                                    <Typography
                                        variant='body2'
                                        sx={{ color: CORAL }}
                                    >
                                        {error}
                                    </Typography>
                                </Box>
                            </Collapse>

                            <Stack spacing={2.5}>
                                {passwordField(
                                    'currentPassword',
                                    'Current password',
                                    showCurrent,
                                    () => setShowCurrent((v) => !v),
                                )}

                                <Stack spacing={0.75}>
                                    <Stack
                                        direction='row'
                                        alignItems='center'
                                        justifyContent='space-between'
                                        flexWrap='wrap'
                                        gap={1}
                                    >
                                        <InputLabel
                                            sx={{
                                                typography: 'body2',
                                                fontWeight: 600,
                                                color: 'text.primary',
                                                mb: 0,
                                            }}
                                        >
                                            New password
                                        </InputLabel>
                                        {newPasswordW ? (
                                            <Stack
                                                direction='row'
                                                alignItems='center'
                                                spacing={1}
                                            >
                                                <Typography
                                                    variant='caption'
                                                    fontWeight={700}
                                                    sx={{
                                                        color: strengthColor(
                                                            theme,
                                                            validCount,
                                                        ),
                                                        textTransform:
                                                            'capitalize',
                                                    }}
                                                >
                                                    {strengthLabel(validCount)}
                                                </Typography>
                                                <Stack
                                                    direction='row'
                                                    spacing={0.5}
                                                    alignItems='center'
                                                >
                                                    {[0, 1, 2, 3].map((i) => (
                                                        <Box
                                                            key={i}
                                                            sx={{
                                                                width: 28,
                                                                height: 4,
                                                                borderRadius: 1,
                                                                bgcolor:
                                                                    i <
                                                                    filledStrengthBars
                                                                        ? strengthColor(
                                                                              theme,
                                                                              validCount,
                                                                          )
                                                                        : alpha(
                                                                              theme
                                                                                  .palette
                                                                                  .text
                                                                                  .disabled,
                                                                              0.35,
                                                                          ),
                                                            }}
                                                        />
                                                    ))}
                                                </Stack>
                                            </Stack>
                                        ) : null}
                                    </Stack>
                                    {passwordField(
                                        'newPassword',
                                        'New password',
                                        showNew,
                                        () => setShowNew((v) => !v),
                                        { hideLabel: true },
                                    )}
                                </Stack>

                                {passwordField(
                                    'confirmNewPassword',
                                    'Confirm new password',
                                    showConfirm,
                                    () => setShowConfirm((v) => !v),
                                    { showMatch: true },
                                )}

                                {requirementsPanel}
                            </Stack>
                        </DialogContent>

                        {footerActions}
                    </Box>
                </form>
            </FormProvider>
        </Dialog>
    );
}
